import { useMemo, useState } from 'react';
import { LaunchArgsManifest, LaunchArgSpec, LaunchArgsValues } from '../../types/database';

// Editor for configured experience launch args (any scope: experience default,
// room, release override, PC override). The form is rendered from the build's
// LaunchArgs.json manifest (switch / value / enum arg specs); it produces a
// PARTIAL values object keyed by arg id — only non-"Unmanaged" fields are
// stored, everything else inherits from the outer scope (ultimately the build
// default). The agent renders the merged values to command-line tokens at
// launch. When no manifest is reachable (no online PC has the build
// installed), a raw JSON editor is the fallback.

// "session" args (room_id, run_id, …) are assembled by the agent on every
// launch and must not be configured.
const configurableArgs = (m: LaunchArgsManifest | null): LaunchArgSpec[] =>
  (m?.args ?? []).filter((a) => a.category !== 'session');

type FormState = Record<string, string>; // arg id -> '' (unmanaged) | 'on'/'off' | enum value | literal

function valuesToForm(specs: LaunchArgSpec[], values: LaunchArgsValues | undefined): FormState {
  const f: FormState = {};
  for (const spec of specs) {
    const v = values?.[spec.id];
    if (v === undefined || v === null) {
      f[spec.id] = '';
    } else if (spec.kind === 'switch') {
      f[spec.id] = v === true ? 'on' : v === false ? 'off' : '';
    } else {
      f[spec.id] = String(v);
    }
  }
  return f;
}

function formToValues(specs: LaunchArgSpec[], form: FormState, rawTokens: string): LaunchArgsValues {
  const out: LaunchArgsValues = {};
  for (const spec of specs) {
    const raw = (form[spec.id] ?? '').trim();
    if (raw === '') continue;
    switch (spec.kind) {
      case 'switch':
        out[spec.id] = raw === 'on';
        break;
      case 'enum':
        out[spec.id] = raw;
        break;
      case 'value': {
        if (spec.valueType === 'int' || spec.valueType === 'float') {
          const n = Number(raw);
          if (Number.isFinite(n)) out[spec.id] = n;
        } else {
          out[spec.id] = raw;
        }
        break;
      }
    }
  }
  const tokens = rawTokens.split(/\s+/).filter(Boolean);
  if (tokens.length > 0) out._raw = tokens;
  return out;
}

// One-line summary of a stored args object, for the scope rows.
export function summarizeLaunchArgs(values: LaunchArgsValues | undefined): string {
  if (!values || Object.keys(values).length === 0) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(values)) {
    if (k === '_raw') continue;
    parts.push(`${k}: ${v === true ? 'on' : v === false ? 'off' : String(v)}`);
  }
  if (Array.isArray(values._raw) && values._raw.length > 0) {
    parts.push(values._raw.join(' '));
  }
  return parts.join(' · ') || 'custom';
}

export default function LaunchArgsForm({
  title,
  manifest,
  initial,
  onSave,
  onClose,
}: {
  title: string;
  manifest: LaunchArgsManifest | null;
  initial: LaunchArgsValues | undefined;
  // null = clear the row (back to inherited / build defaults)
  onSave: (args: LaunchArgsValues | null) => Promise<void>;
  onClose: () => void;
}) {
  const specs = useMemo(() => configurableArgs(manifest), [manifest]);
  const [form, setForm] = useState<FormState>(() => valuesToForm(specs, initial));
  const [rawTokens, setRawTokens] = useState<string>(() =>
    Array.isArray(initial?._raw) ? (initial!._raw as string[]).join(' ') : ''
  );
  // No manifest → raw JSON editing of the whole args object.
  const [rawJson, setRawJson] = useState<string>(() => JSON.stringify(initial ?? {}, null, 2));
  const [saving, setSaving] = useState(false);

  const values = useMemo(() => formToValues(specs, form, rawTokens), [specs, form, rawTokens]);
  const hadValues = !!initial && Object.keys(initial).length > 0;
  // Stored keys the form can't represent (args from another build's manifest,
  // hand-edited rows) would be dropped on save — warn instead of silently
  // rewriting them.
  const lossy = useMemo(() => {
    if (!manifest || !hadValues) return false;
    const known = new Set(specs.map((s) => s.id));
    return Object.keys(initial!).some((k) => k !== '_raw' && !known.has(k));
  }, [manifest, hadValues, initial, specs]);

  const set = (id: string, value: string) => setForm((f) => ({ ...f, [id]: value }));

  const save = async (v: LaunchArgsValues | null) => {
    setSaving(true);
    try {
      await onSave(v);
    } finally {
      setSaving(false);
    }
  };

  const saveFromRawJson = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawJson);
    } catch {
      alert('Invalid JSON');
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      alert('Args must be a JSON object keyed by arg id');
      return;
    }
    const obj = parsed as LaunchArgsValues;
    await save(Object.keys(obj).length > 0 ? obj : null);
  };

  const renderField = (spec: LaunchArgSpec) => {
    const label = spec.label || spec.id;
    const hint = [spec.description, spec.default !== undefined ? `Build default: ${spec.default}` : '']
      .filter(Boolean)
      .join('\n');
    switch (spec.kind) {
      case 'switch':
        return (
          <label key={spec.id} className="software-bs-field" title={hint}>
            {label}
            <select value={form[spec.id] ?? ''} onChange={(e) => set(spec.id, e.target.value)}>
              <option value="">Unmanaged</option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </label>
        );
      case 'enum':
        return (
          <label key={spec.id} className="software-bs-field" title={hint}>
            {label}
            <select value={form[spec.id] ?? ''} onChange={(e) => set(spec.id, e.target.value)}>
              <option value="">Unmanaged</option>
              {(spec.options ?? []).map((o) => (
                <option key={o.value} value={o.value} title={o.description}>
                  {o.value}
                </option>
              ))}
            </select>
          </label>
        );
      case 'value':
        return (
          <label key={spec.id} className="software-bs-field" title={hint}>
            {label}
            <input
              type={spec.valueType === 'int' || spec.valueType === 'float' ? 'number' : 'text'}
              placeholder="Unmanaged"
              value={form[spec.id] ?? ''}
              onChange={(e) => set(spec.id, e.target.value)}
            />
          </label>
        );
      default:
        return null;
    }
  };

  return (
    <div className="software-bs-editor">
      <div className="software-bs-editor-title">{title}</div>

      {manifest ? (
        <>
          <div className="software-bs-fields">
            {specs.map(renderField)}
            <label className="software-bs-field" title="Verbatim extra tokens for args not in the manifest, space-separated (stored as _raw).">
              Raw extra tokens
              <input
                type="text"
                placeholder="e.g. -SomeFlag -Other=1"
                value={rawTokens}
                onChange={(e) => setRawTokens(e.target.value)}
              />
            </label>
          </div>

          {Object.keys(values).length > 0 ? (
            <pre className="software-bs-preview">{JSON.stringify(values, null, 2)}</pre>
          ) : (
            <p className="software-bs-preview-empty">All fields unmanaged — saving clears this scope (back to inherited).</p>
          )}
          {lossy && (
            <p className="software-bs-warning">
              The stored args contain keys this build's manifest does not cover; saving replaces them with the JSON above.
            </p>
          )}

          <div className="software-actions">
            <button disabled={saving} onClick={() => save(Object.keys(values).length > 0 ? values : null)}>
              Save
            </button>
            {hadValues && (
              <button disabled={saving} onClick={() => save(null)}>
                Clear (inherit)
              </button>
            )}
            <button disabled={saving} onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="software-bs-warning">
            No LaunchArgs.json manifest reachable (no online PC has this build installed, or the build ships none) —
            editing the raw args JSON, keyed by arg id, e.g. {'{"dlss":"off","frameRateCap":0,"_raw":["-SomeFlag"]}'}.
          </p>
          <textarea
            className="software-bs-preview"
            rows={8}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            spellCheck={false}
          />
          <div className="software-actions">
            <button disabled={saving} onClick={saveFromRawJson}>
              Save
            </button>
            {hadValues && (
              <button disabled={saving} onClick={() => save(null)}>
                Clear (inherit)
              </button>
            )}
            <button disabled={saving} onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
